import { Video } from '../types/Video';
import {
  generateSortOrderForDate,
  extractDate,
  normalizeToWallClock,
} from './sortOrderComposite';

/**
 * ============================================================================
 * SORT ORDER SYSTEM
 * ============================================================================
 *
 * This file manages the timeline sort order for the video timeline.
 *
 * ## Core Concept: Composite Sort Order
 *
 * Each timeline item has a `sortOrder` field that combines a UTC ISO date
 * with a fractional index into a single sortable string:
 *
 *   "2024-01-15T12:00:00.000Z--a5"
 *    ^^^^^^^^^^^^^^^^^^^^^^^^  ^^
 *    Date portion (grouping)   Fractional index (within-date ordering)
 *
 * - Cross-date ordering: the date prefix handles it lexicographically
 * - Same-date ordering: the fractional index is the tiebreaker
 * - Native string comparison (`<`, `>`) produces correct order
 * - `date_taken` is auto-derived by a database trigger — always in sync
 *
 * ## Visual Display Order
 *
 * - Display is always newest-first (top-left to bottom-right)
 * - This is PURELY visual — sortOrder never changes based on display
 *
 * ## Key Functions
 *
 * - `compareSortOrders()` — Compare two videos by sortOrder
 * - `sortVideosBySortOrder()` — Sort array by sortOrder (ascending)
 * - `assignUniqueSortOrders()` — Assign sortOrders to new uploads
 * - `generateSortOrderRelativeTo()` — from sortOrderComposite.ts, for before/after positioning
 *
 * ## Important Notes
 *
 * - Always use `sortVideosBySortOrder()` instead of manual sorting
 * - Never use `localeCompare()` — requires native string comparison
 * - sortOrder is the single source of truth for both ordering AND grouping
 *
 * ============================================================================
 */

/**
 * Compare two videos by sortOrder using native string comparison (case-sensitive).
 * IMPORTANT: Uses native comparison, NOT localeCompare, as required by fractional-indexing library.
 *
 * Tie-breaking: If sortOrders are equal (shouldn't happen), sort by video ID for deterministic order.
 */
export function compareSortOrders(a: Video, b: Video): number {
  if (a.sortOrder < b.sortOrder) return -1;
  if (a.sortOrder > b.sortOrder) return 1;

  // Tie-breaker: sort by ID if sortOrders are somehow equal
  if (a.id < b.id) return -1;
  if (a.id > b.id) return 1;

  return 0;
}

/**
 * Sort videos by sortOrder using native string comparison.
 * Always use this instead of manual sorting to ensure correct ordering.
 *
 * @param videos - Array of videos to sort
 * @returns Sorted array (new array, does not mutate original)
 */
export function sortVideosBySortOrder(videos: Video[]): Video[] {
  return [...videos].sort(compareSortOrders);
}

/**
 * Assign sort orders to new videos based on their dateTaken timestamps.
 * Maintains chronological order while ensuring correct composite sort_order values.
 *
 * Videos are inserted into the timeline at their chronological position,
 * preserving the order of existing videos.
 *
 * @param newVideos - Array of videos to assign sort orders to
 * @param existingVideos - Array of existing videos in the project
 * @returns Array of videos with sortOrder and derived dateTaken assigned
 */
export function assignUniqueSortOrders(
  newVideos: Video[],
  existingVideos: Video[]
): Video[] {
  // Sort new videos by dateTaken (chronological order)
  const sortedNewVideos = [...newVideos].sort(
    (a, b) => new Date(a.dateTaken).getTime() - new Date(b.dateTaken).getTime()
  );

  // Sort existing videos by sortOrder (native string comparison)
  const sortedExisting = sortVideosBySortOrder(existingVideos);

  // Build the list of existing items for generateSortOrderForDate
  const existingForLookup: { sortOrder: string }[] = sortedExisting.map(v => ({
    sortOrder: v.sortOrder
  }));

  const result: Video[] = [];

  sortedNewVideos.forEach(video => {
    // Generate composite sort_order for this video's date
    const sortOrder = generateSortOrderForDate(video.dateTaken, existingForLookup);

    // Derive dateTaken from sort_order (DB trigger also does this, but keep in sync locally)
    const derivedDate = extractDate(sortOrder);

    const videoWithSortOrder = {
      ...video,
      sortOrder,
      dateTaken: derivedDate,
      originalDateTaken: normalizeToWallClock(video.originalDateTaken),
    };

    // Log insertion for debugging
    console.log(
      `[Sort Order Assignment] ${video.name || video.id}: ` +
      `date=${video.dateTaken} → sortOrder=${sortOrder}`
    );

    result.push(videoWithSortOrder);

    // Insert into existingForLookup to maintain order for next iteration
    const insertPos = existingForLookup.findIndex(item => item.sortOrder > sortOrder);
    const actualInsertPos = insertPos >= 0 ? insertPos : existingForLookup.length;
    existingForLookup.splice(actualInsertPos, 0, { sortOrder });
  });

  return result;
}

